import { useUserStore } from '@/store/userStore'
import { storeToRefs } from 'pinia'
import { useApi } from '@/config/apiFetcher'
import { useUser } from '../useOrgUsers'
import log from '@/utils/useLogger'
import { mqttStore } from '@/store/mqtt'
import { uuid } from '@/helpers'
import { viewsStore } from '@/store/views'
import usePresence, { describeAircraftEvidence, humanisePresence } from '../usePresence'
import { useBrowserNotification } from '../useBrowserNotification'
import {
  describeDeclineOutcome,
  OWNER_COUNTDOWN_SECONDS,
  REQUESTER_RESPONSE_TIMEOUT_MS,
  type TakeoverDeclineReason,
} from '@/constants/takeover'
import {
  acceptedAssignmentMeta,
  buildAssignmentAudit,
  forceReasonFromDecline,
  forcedAssignmentMeta,
  type AssignmentAuditMeta,
  type ForceReason,
} from '@/constants/assignmentAudit'
import { ref } from 'vue'

export interface TakeoverRequest {
  requestId: string
  requestingUserId: string
  requestingUserName: string
  deviceGroupId: string
  deviceGroupName: string
  timestamp: number
  type: 'Takeover' | 'Handover'
  initiator: string
  initiatorId?: string
}

// How long the requester waits for the current owner to answer before
// offering a force takeover itself. Raised from 12s (FLY3D-1029) and now
// derived from the owner's countdown (FLY3D-1023): see constants/takeover.ts.
const TAKEOVER_RESPONSE_TIMEOUT = REQUESTER_RESPONSE_TIMEOUT_MS
interface PendingTakeoverTimeout {
  timeoutId: NodeJS.Timeout
  request: TakeoverRequest
  currentOwnerId: string
}
const pendingTakeoverTimeouts = ref<Map<string, PendingTakeoverTimeout>>(new Map())

export interface TakeoverResponse {
  requestId: string
  response: 'accept' | 'decline'
  currentUserId: string
  timestamp: number
  request: TakeoverRequest
  /** Kept for clients that predate `reason`; mirrors `reason === 'flying'`. */
  isFlying: boolean
  /**
   * Why a decline was sent (FLY3D-1023). Absent on responses from older
   * clients, which are read as an explicit decline unless `isFlying`.
   */
  reason?: TakeoverDeclineReason
}
export interface DeviceGroup {
  deviceGroupID: string
  deviceGroupName: string
  dockLatitude?: number | null
  dockLongitude?: number | null
  dockAltitude?: number | null
  dockHeading?: number | null
  dockDeviceId?: string | null
}
export interface TakeoverState {
  pendingRequests: Map<string, TakeoverRequest>
  showModal: boolean
  currentRequest: TakeoverRequest | null
}

enum ConflictScenario {
  SELF_NO_CONFLICT = 'SELF_NO_CONFLICT',
  SELF_TAKEOVER = 'SELF_TAKEOVER',
  OTHER_DOUBLE_CONFLICT = 'OTHER_DOUBLE_CONFLICT',
  OTHER_HANDOVER = 'OTHER_HANDOVER',
  OTHER_TAKEOVER = 'OTHER_TAKEOVER',
  OTHER_NO_CONFLICT = 'OTHER_NO_CONFLICT',
}
const status = ref('')
export default () => {
  const api = useApi()
  const { allUsers, organisationId, userName, userId } = storeToRefs(useUserStore())
  const { setInfo, clearInfo } = viewsStore()
  const { publishMessage } = mqttStore()
  const { invalidate } = useUser()
  const { fetchUserPresence, isReachable, isConfirmedAbsent } = usePresence()
  const { notify: notifyBrowser } = useBrowserNotification()

  const setStatus = (text: string) => {
    status.value = text
  }
  const determineScenario = (
    isAssigningToSelf: boolean,
    conflictUser: any,
    isTargetUserInGroup: boolean,
  ): ConflictScenario => {
    if (isAssigningToSelf) {
      return conflictUser ? ConflictScenario.SELF_TAKEOVER : ConflictScenario.SELF_NO_CONFLICT
    }
    if (isTargetUserInGroup && conflictUser) return ConflictScenario.OTHER_DOUBLE_CONFLICT
    if (isTargetUserInGroup && !conflictUser) return ConflictScenario.OTHER_HANDOVER
    if (!isTargetUserInGroup && conflictUser) return ConflictScenario.OTHER_TAKEOVER
    return ConflictScenario.OTHER_NO_CONFLICT
  }
  /**
   * Checks if a device group is already assigned to another user
   * Fetches fresh user data to ensure accuracy
   * @param groupToAdd - The device group to check for conflicts
   * @param targetUserId - The ID of the user we want to assign the group to
   * @param targetUserName - The name of the user we want to assign the group to
   * @returns Promise<User | null> - The user object that currently has the device group, or null if no conflict exists
   */
  const checkDeviceGroupConflict = async (
    groupToAdd: DeviceGroup,
    targetUserId: string,
    targetUserName: string,
  ): Promise<{ conflict: boolean; error_message?: string }> => {
    try {
      status.value = 'Fetching updated list of users and assigned groups...'
      // Fetch fresh user data to ensure we have the latest assignments
      invalidate.users()
      setStatus('Checking for device group conflicts...')
      const isAssigningToSelf = userId.value === targetUserId
      const conflictUser = allUsers.value.find((user: any) =>
        user.deviceGroups.some((group: any) => group.deviceGroupID === groupToAdd.deviceGroupID),
      )
      const assignmentMessage = isAssigningToSelf
        ? "You'll be automatically assigned and notified."
        : `${targetUserName} will be automatically assigned and notified.`

      let title = 'Device Group Already Assigned'
      let message = `The device group "${groupToAdd.deviceGroupName}" is currently assigned to ${conflictUser?.name}. A takeover request has been sent to them. They have ${OWNER_COUNTDOWN_SECONDS} seconds to respond.\n\n If they allow: ${assignmentMessage}\n If they decline or do not respond: you will be shown what we know about their status and can force the takeover.`
      const scenario = determineScenario(
        isAssigningToSelf,
        conflictUser,
        allUsers.value.find((user: any) => user.userID === targetUserId)?.deviceGroups.length > 0,
      )
      switch (scenario) {
        case ConflictScenario.SELF_NO_CONFLICT:
          setStatus('')
          return { conflict: false }
        case ConflictScenario.SELF_TAKEOVER: {
          const isOnline = await evaluateOwnerPresence(
            groupToAdd.deviceGroupID,
            groupToAdd.deviceGroupName,
            conflictUser.userID,
            conflictUser.name,
            targetUserId,
            targetUserName,
          )
          if (!isOnline) {
            setStatus('')
            return { conflict: true }
          }
          setStatus('Sending takeover request...')
          await sendTakeOverRequest(
            groupToAdd.deviceGroupID,
            groupToAdd.deviceGroupName,
            conflictUser.userID,
            targetUserId,
            targetUserName,
            'Takeover',
          )
          setStatus('')
          setInfo(title, message)
          return { conflict: true }
        }
        case ConflictScenario.OTHER_DOUBLE_CONFLICT:
          // check if the person trying to assign is the current owner of the device group
          if (conflictUser?.userID === userId.value)
            return handleHandoverConflict(
              groupToAdd.deviceGroupID,
              groupToAdd.deviceGroupName,
              targetUserId,
              targetUserName,
            )
          setStatus('')
          setInfo(
            'Device Group Assignment Conflict',
            `The user ${targetUserName} is already assigned to another device group and the device group "${groupToAdd.deviceGroupName}" is assigned to ${conflictUser?.name}. ${targetUserName} will need to make the takeover request themselves. Only administrators can override this process.`,
          )
          return { conflict: true }
        case ConflictScenario.OTHER_HANDOVER:
          return handleHandoverConflict(
            groupToAdd.deviceGroupID,
            groupToAdd.deviceGroupName,
            targetUserId,
            targetUserName,
          )
        case ConflictScenario.OTHER_TAKEOVER: {
          // Check if the person assigning is the current owner of the device group
          if (conflictUser?.userID === userId.value) {
            setStatus('')
            return { conflict: false }
          }
          const isUserOnline = await evaluateOwnerPresence(
            groupToAdd.deviceGroupID,
            groupToAdd.deviceGroupName,
            conflictUser.userID,
            conflictUser.name,
            targetUserId,
            targetUserName,
          )
          if (!isUserOnline) {
            setStatus('')
            return { conflict: true }
          }
          setStatus('Sending takeover request...')
          await sendTakeOverRequest(
            groupToAdd.deviceGroupID,
            groupToAdd.deviceGroupName,
            conflictUser.userID,
            targetUserId,
            targetUserName,
            'Takeover',
          )
          setStatus('')
          setInfo(title, message)
          return { conflict: true }
        }
        case ConflictScenario.OTHER_NO_CONFLICT:
          setStatus('')
          return { conflict: false }
        default:
          setStatus('')
          return { conflict: false }
      }
    } catch (error) {
      setStatus('')
      log.error('Error checking device group conflict:', error)
      return { conflict: false, error_message: 'Error checking device group conflict' }
    }
  }
  const evaluateOwnerPresence = async (
    deviceGroupID: string,
    deviceGroupName: string,
    currentOwnerId: string,
    currentOwnerName: string,
    requestingUserId: string,
    requestingUserName: string,
  ): Promise<boolean> => {
    setStatus(`Checking whether ${currentOwnerName} is online...`)
    const presence = await fetchUserPresence(currentOwnerId)

    if (isReachable(presence.state)) {
      setStatus(`${currentOwnerName} is connected`)
      return true
    }

    if (!isConfirmedAbsent(presence.state)) {
      // stale or unknown: uncertainty, not absence. Send the request and let
      // the response window decide, rather than asserting they have left.
      log.info(`presence for ${currentOwnerId} is ${presence.state}; sending request anyway`)
      setStatus(`Cannot confirm ${currentOwnerName} is present, sending request anyway...`)
      return true
    }

    setStatus('')
    setInfo(
      `${currentOwnerName} appears to be offline`,
      [
        humanisePresence(presence, currentOwnerName),
        describeAircraftEvidence(presence),
        `Forcing the takeover will assign "${deviceGroupName}" to ${requestingUserName} immediately and notify ${currentOwnerName}.`,
      ]
        .filter(Boolean)
        .join('\n\n'),
      // 0 keeps the prompt up. It used to pass `undefined` here, which took
      // the 15s default and silently dismissed the force-takeover prompt
      // mid-decision.
      0,
      'Force Takeover',
      () =>
        forceTakeover(
          {
            requestId: uuid(10),
            requestingUserId: requestingUserId,
            requestingUserName: requestingUserName,
            deviceGroupId: deviceGroupID,
            deviceGroupName: deviceGroupName,
            timestamp: Date.now(),
            type: 'Takeover',
            initiator: userName.value || 'Unknown',
          },
          currentOwnerId,
          'owner_offline',
        ),
    )
    return false
  }

  const handleHandoverConflict = async (
    groupId: string,
    groupName: string,
    targetUserId: string,
    targetUserName: string,
  ) => {
    setStatus(`Checking whether ${targetUserName} is online...`)
    const presence = await fetchUserPresence(targetUserId)
    if (isConfirmedAbsent(presence.state)) {
      // A handover needs the recipient to accept, so there is no point sending
      // one to someone the broker has seen disconnect. Uncertain states still
      // get the request: silence is not a confirmed absence.
      setStatus('')
      setInfo(
        `${targetUserName} appears to be offline`,
        `${humanisePresence(presence, targetUserName)}\n\nA handover needs them to accept it, so it has not been sent. Try again once they are back.`,
        0,
      )
      return { conflict: true }
    }
    setStatus('Sending handover request...')
    await sendTakeOverRequest(
      groupId,
      groupName,
      targetUserId,
      targetUserId,
      targetUserName,
      'Handover',
    )
    setStatus('')
    setInfo(
      'Device HandOver Conflict',
      `The user ${targetUserName} is already assigned to another device group. A handover has being initiated, if they accept, they will be removed from their current device group and assigned to "${groupName}". Only administrators can override this process.`,
    )
    return { conflict: true }
  }
  /**
   * Assigns a user to a device group directly (without conflict checking)
   * @param userId - The ID of the user to assign to the device group
   * @param deviceGroupId - The ID of the device group
   * @param deviceGroupName - The name of the device group
   * @param currentUserId - The previous owner to notify over MQTT, if any
   * @param audit - How this assignment came about (IDMETA-6). The backend
   *   records it in the handover audit table; omitted means "unspecified".
   * @returns Promise<void> - Resolves when assignment is complete
   * @throws Error if the API request fails
   */
  const addUserToGroup = async (
    user_id: string,
    userNameToAssign: string,
    deviceGroupId: string,
    deviceGroupName: string,
    currentUserId?: string,
    audit?: AssignmentAuditMeta,
  ): Promise<void> => {
    try {
      setStatus('Assigning user to device group...')
      const url = `${import.meta.env.VITE_APP_HTTP_ENPOINT}/device-management/assign-device-group`
      await api({
        url,
        method: 'post',
        data: {
          organisationID: organisationId.value,
          deviceGroupID: deviceGroupId,
          deviceGroupName: deviceGroupName,
          userID: user_id,
          userName: userNameToAssign,
          ...(audit ? { audit: buildAssignmentAudit(audit) } : {}),
        },
      })
      setStatus('Updating user and device group lists...')
      invalidate.users()
      invalidate.deviceGroupIds()

      if (currentUserId && currentUserId !== user_id) {
        setStatus('Sending notification to previously assigned user...')
        const assignedUser = allUsers.value.find((user: any) => user.userID === user_id)
        const notificationMessage = {
          deviceGroupId,
          deviceGroupName,
          newOwnerId: user_id,
          newOwnerName: assignedUser?.name || userName.value,
          timestamp: Date.now(),
        }
        const topic = `device-group-assignment-changed/${currentUserId}`
        const success = await publishMessage(topic, JSON.stringify(notificationMessage), 1)
        if (!success) {
          setStatus('')
          const error = new Error('Failed to send takeover response')
          log.error('Failed to send takeover response:', error)
          throw error
        }
        setStatus('')
      }
    } catch (err: any) {
      setStatus('')
      log.error(`Add user to group error`, err)
      throw err
    }
  }
  /**
   * Starts a timeout for a pending takeover request
   * If no response is received within the timeout period, shows force takeover modal
   * @param request - The takeover request
   * @param currentOwnerId - The user ID of the current owner
   */
  const startTakeoverTimeout = (request: TakeoverRequest, currentOwnerId: string) => {
    // Clear any existing timeout for this request
    cancelTakeoverTimeout(request.requestId)

    const timeoutId = setTimeout(() => {
      log.info(`Takeover request ${request.requestId} timed out - no response received`)
      pendingTakeoverTimeouts.value.delete(request.requestId)

      // Show force takeover modal since user didn't respond
      setInfo(
        'No Response Received',
        `The user did not respond to the ${request.type.toLowerCase()} request within the time limit. They may have refreshed or closed their browser. You can force the takeover.`,
        undefined,
        'Force Takeover',
        () => forceTakeover(request, currentOwnerId, 'no_response'),
      )
      // Alert the requester even if they tabbed away while waiting (FLY3D-866).
      notifyBrowser({
        title: 'No Response Received',
        body: `No response to your ${request.type.toLowerCase()} request for "${request.deviceGroupName}". You can force the takeover.`,
        tag: 'device-group-takeover-outcome',
      })
    }, TAKEOVER_RESPONSE_TIMEOUT)

    pendingTakeoverTimeouts.value.set(request.requestId, {
      timeoutId,
      request,
      currentOwnerId,
    })

    log.info(
      `Started ${TAKEOVER_RESPONSE_TIMEOUT / 1000}s timeout for takeover request ${request.requestId}`,
    )
  }

  /**
   * Cancels a pending takeover timeout
   * @param requestId - The request ID to cancel timeout for
   */
  const cancelTakeoverTimeout = (requestId: string) => {
    const pending = pendingTakeoverTimeouts.value.get(requestId)
    if (pending) {
      clearTimeout(pending.timeoutId)
      pendingTakeoverTimeouts.value.delete(requestId)
      log.info(`Cancelled timeout for takeover request ${requestId}`)
    }
  }

  /**
   * Sends a takeover request to the current owner of a device group via MQTT
   * @param deviceGroupID - The ID of the device group to request takeover for
   * @param deviceGroupName - The name of the device group to request takeover for
   * @param currentOwnerId - The user ID of the current owner who will receive the request
   * @param requestingUserId - The ID of the user who wants to be assigned to the group
   * @param requestingUserName - The name of the user who wants to be assigned to the group
   * @returns Promise<string> - Returns the request ID for tracking purposes
   * @throws Error if MQTT client is not available or if request fails
   */
  const sendTakeOverRequest = async (
    deviceGroupID: string,
    deviceGroupName: string,
    currentOwnerId: string,
    requestingUserId: string,
    requestingUserName: string,
    type: 'Takeover' | 'Handover',
  ): Promise<string> => {
    try {
      setStatus(`Sending ${type.toLowerCase()} request...`)
      const requestId = uuid(10)

      const request: TakeoverRequest = {
        requestId,
        requestingUserId: requestingUserId,
        requestingUserName: requestingUserName,
        timestamp: Date.now(),
        deviceGroupId: deviceGroupID,
        deviceGroupName: deviceGroupName,
        type,
        initiator: userName.value || 'Unknown',
      }
      if (type === 'Handover') request.initiatorId = userId.value || ''

      const topic = `device-group-takeover-request/${currentOwnerId}`

      const success = await publishMessage(topic, JSON.stringify(request), 1)

      if (success) {
        setStatus(`${type} request sent successfully`)
        log.info(
          `${type} request ${requestId} sent to ${currentOwnerId} for device group ${deviceGroupName}`,
        )

        // Start timeout for this request - if no response in 12 seconds, show force takeover
        startTakeoverTimeout(request, currentOwnerId)

        return requestId
      } else {
        setStatus('')
        const error = new Error(`Failed to send ${type} request`)
        log.error(`Failed to send ${type} request:`, error)
        throw error
      }
    } catch (err) {
      setStatus('')
      log.error('Error in sendTakeOverRequest:', err)
      throw err
    }
  }

  /**
   * Sends a response to a takeover request via MQTT
   * @param requestId - The ID of the original takeover request
   * @param response - The response ('accept' or 'decline')
   * @param reason - For declines, why it was declined. Defaults to an explicit
   *   user decline. Ignored for accepts.
   * @returns Promise<void> - Resolves when response is sent
   * @throws Error if MQTT client is not available or if response fails
   */
  const sendTakeOverResponse = async (
    request: TakeoverRequest,
    response: 'accept' | 'decline',
    reason: TakeoverDeclineReason = 'user',
  ): Promise<void> => {
    try {
      if (!userId.value || !userName.value) {
        throw new Error('User details are incomplete')
      }

      const responseObj: TakeoverResponse = {
        requestId: request.requestId,
        response,
        currentUserId: userId.value,
        timestamp: Date.now(),
        request,
        isFlying: response === 'decline' && reason === 'flying',
        ...(response === 'decline' ? { reason } : {}),
      }
      const id = request.initiatorId ? request.initiatorId : request.requestingUserId
      const topic = `device-group-takeover-response/${id}`

      const success = await publishMessage(topic, JSON.stringify(responseObj), 1)

      if (success) {
        log.info(
          `${request.type} response ${request.requestId} sent to ${id} for device group ${request.deviceGroupName}`,
        )
      } else {
        const error = new Error(`Failed to send ${request.type} response`)
        log.error(`Failed to send ${request.type} response:`, error)
        throw error
      }
    } catch (err) {
      log.error('Error in sendTakeOverResponse:', err)
      throw err
    }
  }

  /**
   * Handles the takeover response and performs appropriate actions
   * @param response - The takeover response object containing response details
   * @param originalRequest - The original takeover request for context
   * @returns Promise<void> - Resolves when all actions are complete
   */
  const handleTakeoverResponse = async (response: TakeoverResponse): Promise<void> => {
    try {
      const originalRequest = response.request

      // Cancel the timeout since we received a response
      cancelTakeoverTimeout(originalRequest.requestId)
      // Also clear any info modal that might be showing
      clearInfo()

      if (response.response === 'accept') {
        // User accepted - add them to the device group
        await addUserToGroup(
          originalRequest.requestingUserId,
          originalRequest.requestingUserName,
          originalRequest.deviceGroupId,
          originalRequest.deviceGroupName,
          response.currentUserId,
          acceptedAssignmentMeta(originalRequest, response.currentUserId),
        )

        // Notify the previous owner that their device group was taken over
        log.info('sending notification message to previous owner')
        const notificationMessage = {
          deviceGroupId: originalRequest.deviceGroupId,
          deviceGroupName: originalRequest.deviceGroupName,
          newOwnerId: originalRequest.requestingUserId,
          newOwnerName: originalRequest.requestingUserName,
          timestamp: Date.now(),
        }
        const success = await publishMessage(
          `device-group-assignment-changed/${response.currentUserId}`,
          JSON.stringify(notificationMessage),
          1,
        )
        if (!success) {
          const error = new Error(`Failed to send ${response.request.type} response`)
          log.error(`Failed to send ${response.request.type} response:`, error)
          throw error
        }

        // Show success info modal
        if (response.request.type === 'Takeover') {
          setInfo(
            'Takeover Successful!',
            `Great! The user allowed you to take over the "${originalRequest.deviceGroupName}" device group. You have been automatically assigned to this group and can now access its devices.`,
          )
        } else {
          setInfo(
            'Handover Successful!',
            `Great! The user accepted the handover of the "${originalRequest.deviceGroupName}" device group. They have been automatically assigned to this group and can now access its devices.`,
          )
        }
        // Notify the requester of the outcome in case they tabbed away (FLY3D-866).
        notifyBrowser({
          title: `${response.request.type} Successful`,
          body: `Your ${response.request.type.toLowerCase()} request for "${originalRequest.deviceGroupName}" was accepted.`,
          tag: 'device-group-takeover-outcome',
        })
        log.info(
          `${response.request.type} request ${originalRequest.requestId} successful for ${originalRequest.deviceGroupName}`,
        )
      } else {
        // Declined. Say *why* (FLY3D-1023): an owner who never saw the modal
        // must not be reported as having refused.
        const outcome = describeDeclineOutcome(response)
        setInfo(
          outcome.title,
          outcome.message,
          undefined,
          'Force Takeover',
          () =>
            forceTakeover(originalRequest, response.currentUserId, forceReasonFromDecline(response)),
        )
        // Notify the requester of the outcome in case they tabbed away (FLY3D-866).
        notifyBrowser({
          title: outcome.notificationTitle,
          body: outcome.notificationBody,
          tag: 'device-group-takeover-outcome',
        })
        log.info(
          `${response.request.type} request ${originalRequest.requestId} declined for ${originalRequest.deviceGroupName} (reason: ${response.reason ?? 'unspecified'})`,
        )
      }
    } catch (error) {
      log.error(`Error handling takeover ${response.request.type}:`, error)
      // Show error info modal
      setInfo(
        `${response.request.type} Error`,
        `An error occurred while processing the takeover response for "${response.request.deviceGroupName}". Please try again or contact an administrator.`,
      )
    }
  }
  /**
   * Assigns the group without the owner's consent.
   * @param reason - What led here (owner offline, no reply, a decline...).
   *   Recorded in the backend's handover audit trail (IDMETA-6).
   */
  const forceTakeover = async (
    request: TakeoverRequest,
    currentUserId: string,
    reason: ForceReason,
  ) => {
    try {
      clearInfo()
      await addUserToGroup(
        request.requestingUserId,
        request.requestingUserName,
        request.deviceGroupId,
        request.deviceGroupName,
        currentUserId,
        forcedAssignmentMeta(request, currentUserId, reason),
      )
      setInfo(
        'Force Takeover Successful',
        `The device group "${request.deviceGroupName}" has been forcefully assigned to ${request.requestingUserName}.`,
      )
    } catch (error) {
      log.error(`Error forcing takeover:`, error)
      setInfo(
        'Force Takeover Failed',
        `Failed to force takeover of device group "${request.deviceGroupName}". Please try again or contact an administrator.`,
      )
      throw error
    }
  }
  return {
    checkDeviceGroupConflict,
    addUserToGroup,
    sendTakeOverRequest,
    sendTakeOverResponse,
    handleTakeoverResponse,
    cancelTakeoverTimeout,
    status,
  }
}
